import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../../services/adminService';
import { useNotifications } from '../../utils/notificationUtils';
import EnrollmentPaymentControls from './EnrollmentPaymentControls';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TextField,
    InputAdornment,
    Button,
    CircularProgress,
    Alert,
    Pagination
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

const SessionPayments = () => {
    const { showError } = useNotifications();
    const [payments, setPayments] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, totalPages: 1 });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('unpaid');

    const fetchPayments = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: pagination.limit,
                search: searchTerm || undefined
            };
            if (filter === 'unpaid') {
                params.unpaid = '1';
            } else if (filter !== 'all') {
                params.payment_status = filter;
            }
            const data = await adminService.getSessionPayments(params);
            setPayments(data.payments || []);
            setPagination(data.pagination || pagination);
        } catch (err) {
            showError(err.message || 'Failed to load session payments');
        } finally {
            setLoading(false);
        }
    }, [filter, searchTerm, pagination.limit, showError]);

    useEffect(() => {
        fetchPayments(1);
    }, [filter]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchPayments(1);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString();
    };

    const formatAmount = (amount) => {
        const n = Number(amount);
        if (Number.isNaN(n) || n === 0) return 'FREE';
        return `$${n.toFixed(2)}`;
    };

    const unpaidCount = filter === 'unpaid' ? pagination.total : null;

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight={600}>
                        Session Payments
                    </Typography>
                    {filter === 'unpaid' && unpaidCount !== null && (
                        <Typography variant="body2" color="text.secondary">
                            {unpaidCount} student{unpaidCount !== 1 ? 's' : ''} approved but unpaid
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {[
                        { key: 'unpaid', label: 'Unpaid' },
                        { key: 'paid', label: 'Paid' },
                        { key: 'waived', label: 'Waived' },
                        { key: 'all', label: 'All' }
                    ].map(({ key, label }) => (
                        <Button
                            key={key}
                            size="small"
                            variant={filter === key ? 'contained' : 'outlined'}
                            onClick={() => setFilter(key)}
                        >
                            {label}
                        </Button>
                    ))}
                </Box>
            </Box>

            <Box component="form" onSubmit={handleSearch} sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search student, email, or class..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                    sx={{ minWidth: 280, mr: 1 }}
                />
                <Button type="submit" variant="outlined" size="small">
                    Search
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : payments.length === 0 ? (
                <Alert severity="info">No enrollments match this filter.</Alert>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Class</TableCell>
                                    <TableCell>Session</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Paid date</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((row) => (
                                    <TableRow key={row.enrollment_id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.student_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {row.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{row.class_name}</TableCell>
                                        <TableCell>{formatDate(row.session_date)}</TableCell>
                                        <TableCell>{formatAmount(row.amount)}</TableCell>
                                        <TableCell>
                                            <EnrollmentPaymentControls
                                                enrollmentId={row.enrollment_id}
                                                paymentStatus={row.payment_status}
                                                paymentMethod={row.payment_method}
                                                paidAt={row.paid_at}
                                                paymentNotes={row.payment_notes}
                                                disabled={row.enrollment_status === 'rejected'}
                                                compact
                                                onUpdated={() => fetchPayments(pagination.page)}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(row.paid_at)}</TableCell>
                                        <TableCell>
                                            {row.payment_notes ? (
                                                <Typography variant="caption">{row.payment_notes}</Typography>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {pagination.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pagination.totalPages}
                                page={pagination.page}
                                onChange={(_, p) => fetchPayments(p)}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default SessionPayments;
